// src/pages/guia/GuiaLiquidaciones.js
//
// Histórico de liquidaciones del Guía (bruto, retención, neto, estado).

import React, { useEffect, useState } from 'react';
import {
  Container, Typography, Card, Table, TableHead, TableRow, TableCell,
  TableBody, Chip, Alert, LinearProgress, Stack, Box, Button,
  Dialog, DialogTitle, DialogContent, DialogActions, Divider,
} from '@mui/material';
import PaymentsIcon from '@mui/icons-material/Payments';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { guia as api } from '../../api/guiaApi';
import { useApp } from '../../context/AppContext';

const fmtCOP = (n) => new Intl.NumberFormat('es-CO', {
  style: 'currency', currency: 'COP', maximumFractionDigits: 0 }).format(n || 0);
const colorEstado = (e) => e === 'PAGADA' ? 'success' :
                           e === 'CALCULADA' ? 'info' :
                           e === 'APROBADA' ? 'primary' : 'default';

export default function GuiaLiquidaciones() {
  const { notificar } = useApp();
  const [lista, setLista] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [detalle, setDetalle] = useState(null);

  useEffect(() => {
    api.liquidaciones()
      .then(r => setLista(r || []))
      .catch(e => notificar(e.mensajeUsuario || 'Error.', 'error'))
      .finally(() => setCargando(false));
  }, [notificar]);

  const abrirDetalle = async (id) => {
    try { setDetalle(await api.detalleLiquidacion(id)); }
    catch (e) { notificar(e.mensajeUsuario || 'Error.', 'error'); }
  };

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Stack direction="row" alignItems="center" spacing={1} mb={3}>
        <PaymentsIcon color="primary" sx={{ fontSize: 32 }} />
        <Box>
          <Typography variant="h4" fontFamily="Fraunces">Mis liquidaciones</Typography>
          <Typography variant="body2" color="text.secondary">
            Cada período consolida tus comisiones y la retención en la fuente.
          </Typography>
        </Box>
      </Stack>

      {cargando ? <LinearProgress /> : lista.length === 0 ? (
        <Alert severity="info">Aún no tienes liquidaciones generadas.</Alert>
      ) : (
        <Card>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Período</TableCell>
                <TableCell align="right">Comisiones</TableCell>
                <TableCell align="right">Bruto</TableCell>
                <TableCell align="right">Retención</TableCell>
                <TableCell align="right">Neto</TableCell>
                <TableCell align="center">Estado</TableCell>
                <TableCell>Pagada</TableCell>
                <TableCell align="right"></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {lista.map(l => (
                <TableRow key={l.liquidacionId} hover>
                  <TableCell>
                    <Typography variant="caption">
                      {l.periodoDesde?.substring(0,10)} → {l.periodoHasta?.substring(0,10)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">{fmtCOP(l.subtotalComisiones)}</TableCell>
                  <TableCell align="right">{fmtCOP(l.bruto)}</TableCell>
                  <TableCell align="right" sx={{ color: 'error.main' }}>
                    -{fmtCOP(l.montoRetefuente)}
                  </TableCell>
                  <TableCell align="right"><b>{fmtCOP(l.neto)}</b></TableCell>
                  <TableCell align="center">
                    <Chip label={l.estado} size="small" color={colorEstado(l.estado)} />
                  </TableCell>
                  <TableCell>{l.fechaPago ? l.fechaPago.substring(0,10) : '—'}</TableCell>
                  <TableCell align="right">
                    <Button size="small" startIcon={<VisibilityIcon />}
                            onClick={() => abrirDetalle(l.liquidacionId)}>
                      Ver
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      <Dialog open={!!detalle} onClose={() => setDetalle(null)} maxWidth="md" fullWidth>
        <DialogTitle>Detalle de liquidación</DialogTitle>
        <DialogContent dividers>
          {detalle && (
            <>
              <Typography variant="body2" color="text.secondary" mb={1}>
                {detalle.periodoDesde?.substring(0,10)} → {detalle.periodoHasta?.substring(0,10)}
              </Typography>
              <Stack direction="row" spacing={3} mb={2}>
                <Box><Typography variant="caption">Bruto</Typography>
                  <Typography variant="h6">{fmtCOP(detalle.bruto)}</Typography></Box>
                <Box><Typography variant="caption">Retención ({detalle.porcentajeRetefuente}%)</Typography>
                  <Typography variant="h6" color="error.main">-{fmtCOP(detalle.montoRetefuente)}</Typography></Box>
                <Box><Typography variant="caption">Neto</Typography>
                  <Typography variant="h6" color="success.main">{fmtCOP(detalle.neto)}</Typography></Box>
              </Stack>
              {detalle.estado === 'PAGADA' && (
                <Alert severity="success" sx={{ mb: 2 }}>
                  Pagada el {detalle.fechaPago?.substring(0,10)} · {detalle.medioPago}
                  {detalle.referenciaPago && ` · Ref: ${detalle.referenciaPago}`}
                </Alert>
              )}
              <Divider sx={{ mb: 2 }} />
              <Typography variant="overline">Desglose por afiliado</Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Negocio</TableCell><TableCell>Factura</TableCell>
                    <TableCell>Producto</TableCell>
                    <TableCell align="right">Base</TableCell>
                    <TableCell align="right">%</TableCell>
                    <TableCell align="right">Comisión</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {detalle.detalles.map(d => (
                    <TableRow key={d.detalleId}>
                      <TableCell>{d.nombreComercial || d.empresa}</TableCell>
                      <TableCell><Typography variant="caption">{d.numeroFactura}</Typography></TableCell>
                      <TableCell>{d.producto}</TableCell>
                      <TableCell align="right">{fmtCOP(d.baseGravable)}</TableCell>
                      <TableCell align="right">{d.porcentajeComision}%</TableCell>
                      <TableCell align="right"><b>{fmtCOP(d.comision)}</b></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetalle(null)}>Cerrar</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
